import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Icon from '../components/common/Icon';
import Breadcrumbs from '../components/common/Breadcrumbs';
import SectionHeader from '../components/common/SectionHeader';

const campData = {
  'casa-studenti': {
    title: 'La Casa è un Diritto',
    status: 'Attiva',
    overline: 'Mobilitazione Nazionale',
    description: "Il caro affitti sta negando il diritto allo studio a migliaia di studenti. Chiediamo che il governo investa seriamente nell'edilizia universitaria pubblica e fermi la speculazione immobiliare nelle città universitarie.",
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=1600&auto=format&fit=crop',
    goals: [
      '100.000 nuovi posti letto pubblici entro il 2026',
      'Tetto agli affitti per studenti fuori sede',
      'Riconversione di immobili pubblici abbandonati',
      'Potenziamento delle borse di studio per il canone'
    ],
    materials: [
      { name: 'Kit Grafico Social', type: 'ZIP' },
      { name: 'Volantino A5 (PDF)', type: 'PDF' },
      { name: 'Documento Politico', type: 'PDF' }
    ]
  },
  'salario-minimo': {
    title: 'Salario Minimo Subito',
    status: 'In corso',
    overline: 'Raccolta Firme',
    description: "In Italia oltre 3 milioni di persone lavorano ma restano povere. È il momento di dire basta. Chiediamo una legge che fissi il salario minimo a 9 euro l'ora per tutti i lavoratori.",
    image: 'https://images.unsplash.com/photo-1540317580384-e5d43616b9aa?q=80&w=1600&auto=format&fit=crop',
    goals: [
      'Soglia minima legale di 9€ l\'ora',
      'Rafforzamento della contrattazione collettiva',
      'Contrasto ai contratti "pirata"',
      'Tutela dei lavoratori stagionali e precari'
    ],
    materials: [
      { name: 'Modulo raccolta firme', type: 'PDF' },
      { name: 'Poster Campagna', type: 'JPG' },
      { name: 'Vademecum per attivisti', type: 'PDF' }
    ]
  }
};

const CampagnaPage = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const id = query.get('id') || 'casa-studenti';
  const data = campData[id] || campData['casa-studenti'];

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Campagne', path: '/campagne' },
    { label: data.title }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  return (
    <Layout>
      <section className="hero--epic">
        <div className="hero__bg">
          <div className="hero__image" style={{ backgroundImage: `url(${data.image})` }}></div>
          <div className="hero__overlay" style={{ background: 'linear-gradient(to bottom, rgba(200, 16, 46, 0.4) 0%, rgba(0,0,0,0.9) 100%)' }}></div>
        </div>
        
        <div className="hero__content-epic container" style={{ color: '#fff' }}>
          <div className="reveal" style={{ marginBottom: 'var(--space-8)' }}>
            <Breadcrumbs items={breadcrumbs} style={{ color: 'rgba(255,255,255,0.7)' }} />
          </div>
          
          <div className="reveal mb-4">
            <span className="badge" style={{ background: '#fff', color: 'var(--color-primary)', border: 'none', fontWeight: 800 }}>{data.status}</span>
          </div>
          
          <h1 className="hero__title-epic text-reveal-trigger" style={{ color: '#fff' }}>
            <span className="line text-reveal"><span className="word text-reveal__inner">{data.title}</span></span>
          </h1>
          
          <p className="hero__subtitle-epic reveal" style={{ color: 'rgba(255,255,255,0.9)', maxWidth: '800px' }}>
            {data.description}
          </p>
        </div>
      </section>

      <section className="section bg-subtle">
        <div className="container">
          <div className="grid grid-2 gap-12 items-center">
            <div className="reveal">
              <SectionHeader overline="Obiettivi" title="Cosa chiediamo" className="mb-8" />
              <div className="grid grid-1 gap-4">
                {data.goals.map((goal, i) => (
                  <div key={i} className="flex gap-4 p-5 bg-white border rounded-2xl shadow-sm reveal stagger-2">
                    <div style={{ color: 'var(--color-primary)' }}><Icon name="Target" size={24} /></div>
                    <p className="text-body font-bold">{goal}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="reveal reveal--scale">
              <div style={{ 
                background: 'var(--color-primary)', 
                padding: 'var(--space-10)', 
                borderRadius: 'var(--radius-3xl)',
                boxShadow: '0 20px 40px rgba(200, 16, 46, 0.3)',
                color: '#fff'
              }}>
                <h3 className="h3 mb-6" style={{ color: '#fff' }}>Scendi in campo</h3>
                <p className="text-body-sm mb-8 opacity-90">
                  Questa campagna ha bisogno di gambe e cuori. Partecipa alla mobilitazione nel tuo circolo o aiutaci a diffondere il materiale.
                </p>
                <div className="flex flex-col gap-4">
                  <button className="btn btn--lg" style={{ background: '#fff', color: 'var(--color-primary)' }}>Firma la proposta</button>
                  <button className="btn btn--outline btn--lg" style={{ borderColor: 'rgba(255,255,255,0.4)', color: '#fff' }}>Trova un evento</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHeader overline="Risorse" title="Materiali scaricabili" className="mb-10" />
          <div className="grid grid-3 gap-6">
            {data.materials.map((mat, i) => (
              <div key={i} className="p-6 border rounded-2xl flex items-center justify-between hover:bg-subtle transition-colors reveal stagger-3">
                <div className="flex items-center gap-4">
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: 'var(--radius-lg)', 
                    background: 'var(--color-bg-subtle)', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center'
                  }}>
                    <Icon name={mat.type === 'PDF' ? 'FileText' : 'Download'} size={24} />
                  </div>
                  <div>
                    <h4 className="h5 mb-0">{mat.name}</h4>
                    <p className="text-small text-secondary">{mat.type}</p>
                  </div>
                </div>
                <button className="btn btn--icon btn--sm btn--outline"><Icon name="Download" size={16} /></button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CampagnaPage;
